/**
 * The arguments and the refusal sentences of the app's writes that go through router tools
 * (app#373, docs/PERMISSIONS.md § 9: "the app writes through the router").
 *
 * Pure, so `routerWrites.test.ts` can check them with `node --experimental-strip-types`. The
 * stores call `callTool` with what these return; nothing here imports the tools client.
 *
 * ⚠⚠ BEFORE app#373 THESE WERE POSTGREST WRITES, and the database decided nothing about WHO. Any
 * Member could upsert any column of any service of the org, re-channel any node and approve any
 * access request. The router asks the node's controllers (the node's owner, or an org Owner/Admin)
 * at each call, so the same click now goes to the one place that knows the rule.
 */

/** What the service form produces. `ServiceForm.tsx` re-exports it. */
export interface ServiceFormData {
  title: string
  url: string | null
  command: string | null
  admin: boolean
  workdir: string | null
  authRequired: boolean
}

/**
 * `service_publish` arguments for a new service on `nodeId`.
 *
 * ⚠ A web service is a proxy (`proxy_target_url`); a terminal sends `terminal_command` only when
 * the form has one, because an absent command is the node's login shell.
 * ⚠ `admin` is sent as a plain BOOLEAN, and only for a terminal: on `service_publish` it records an
 * identity and runs nothing, so the type is not the string of the executing tools.
 * ⚠ `auth_required` is always sent. The router defaults it to true, but the form is what decides.
 */
export function servicePublishArgs(nodeId: string, form: ServiceFormData): Record<string, unknown> {
  const args: Record<string, unknown> = {
    node_id: nodeId,
    title: form.title,
    enabled: true,
    auth_required: form.authRequired,
  }
  if (form.url !== null) {
    args.proxy_target_url = form.url
    return args
  }
  if (form.command) args.terminal_command = form.command
  args.admin = form.admin
  if (form.workdir) args.workdir = form.workdir
  return args
}

/**
 * `service_update` arguments: only what the form CHANGED, or null when nothing changed.
 *
 * ⚠⚠ NEVER A `node_id`: a service stays on its node, and `service_update` has no such field.
 * ⚠⚠ `admin: false` IS SENT, NEVER LEFT OUT, TO LOWER AN ELEVATED TERMINAL. On `service_update` an
 * omitted `admin` KEEPS the stored identity, so leaving it out would save a form that says "not
 * elevated" over a service that still runs as root.
 * ⚠ Only changed fields, because the router asks for root on an elevated terminal unless the
 * change only narrows it (title, `auth_required: true`). Resending an unchanged command would
 * turn a retitle into a change that needs root.
 * ⚠ An emptied terminal command is `""` (the login shell) and an emptied working directory is
 * `""` (the home directory): on `service_update` an absent field means "keep".
 */
export function serviceUpdateArgs(service: Service, form: ServiceFormData): Record<string, unknown> | null {
  const args: Record<string, unknown> = {}
  if ((service.title ?? '') !== form.title) args.title = form.title
  if (form.authRequired !== service.authRequired) args.auth_required = form.authRequired
  if (form.url !== null) {
    if (form.url !== (service.url ?? '')) args.proxy_target_url = form.url
  } else {
    if ((form.command ?? '') !== (service.command ?? '')) args.terminal_command = form.command ?? ''
    if (form.admin !== service.admin) args.admin = form.admin
    if ((form.workdir ?? '') !== (service.workdir ?? '')) args.workdir = form.workdir ?? ''
  }
  if (!Object.keys(args).length) return null
  return { service_id: service.id, ...args }
}

/** A router tool error as `callTool` throws it (`ToolCallError`), read by shape. */
type ToolFailure = { code?: string; message?: string; details?: unknown } | null | undefined

/**
 * One sentence for a failed router write.
 *
 * `forbidden` is the gate's answer. The router's own text for it names a Go gate, so the caller
 * gives the sentence for its subject. Every other refusal (`already_exists`, `invalid_args`,
 * `failed_precondition`, `not_found`) is written for the reader and names what to do, so it is
 * shown as it is. A failure with no text reads as `fallback`.
 */
export function toolRefusalMessage(error: unknown, forbidden: string, fallback: string): string {
  const failure = error as ToolFailure
  if (failure?.code === 'forbidden') return forbidden
  // ⚠ Two spellings of one fact: `unauthenticated` is callTool's own (no token in the browser), and
  // `unauthorized` is the router's 401 (`CodeUnauthorized`), whose text names a bearer token.
  if (failure?.code === 'unauthenticated' || failure?.code === 'unauthorized') {
    return 'Your session has ended. Sign in again, then try once more.'
  }
  return failure?.message || fallback
}

/** The sentence a reader who is not the node's controller gets, for any service change. */
export const SERVICE_FORBIDDEN =
  'Only the owner of this node, or an owner or admin of its organization, can change its services.'

/**
 * `service_share approve` arguments for one access request.
 *
 * ⚠⚠ `confirm_root` IS SENT ONLY AFTER THE READER CONFIRMED THE ROOT DIALOG (docs/PERMISSIONS.md
 * § 5 Services, D-40; § 4.1, D-3: root is never given silently). The first call never carries it:
 * the router is the one that knows if the approval gives root, and it says so by refusing.
 */
export function approveArgs(
  grant: { id: string; serviceId: string },
  confirmedRoot: boolean
): Record<string, unknown> {
  const args: Record<string, unknown> = { service_id: grant.serviceId, action: 'approve', grant_id: grant.id }
  if (confirmedRoot) args.confirm_root = true
  return args
}

/** What the root dialog must tell the reader about the requester. */
export type RootApproval = {
  /** The requester is not a member of the node's organization. The router allows it and says so. */
  outsideOrg: boolean
}

/**
 * Reads a refused `service_share approve`: non-null when the router asks for the root
 * confirmation, null for every other refusal.
 *
 * ⚠ On a ROOT terminal, approving a requester who does not control the node names them on it, and
 * the name gives a root shell on the node (D-40). The router refuses that with
 * `failed_precondition` unless the call sends `confirm_root: true`. Before app#461 the app could
 * not send it: Accept failed each time, and the page showed the router's text for an API caller.
 * ⚠ The facts come from the refusal's `details` (`confirm_root_required`, `outside_org`;
 * router#1982). A router before that sends none, and then they are read from its text, by two
 * markers that a router test pins: "Send confirm_root" and "NOT a member of the node". If neither
 * reads, the request stays open and `approveRefusal` gives the app's own sentence.
 */
export function rootApprovalNeeded(error: unknown): RootApproval | null {
  const failure = error as ToolFailure
  if (failure?.code !== 'failed_precondition') return null
  const details = record(failure.details)
  const text = failure.message ?? ''
  // ⚠ The text marker counts WITH the details, not only without them: the warning about a person
  // outside the organization must not be lost because `outside_org` came in a shape the app does
  // not know.
  const outsideInText = text.includes('NOT a member of the node')
  if (details?.confirm_root_required === true) {
    return { outsideOrg: (Array.isArray(details.outside_org) && details.outside_org.length > 0) || outsideInText }
  }
  if (!text.includes('Send confirm_root')) return null
  return { outsideOrg: outsideInText }
}

const record = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null

/**
 * true when the app must NOT show a request as granted before the router answers (app#463): the
 * first Accept on a root terminal. The router refuses that call to ask for the confirmation, so
 * an early "granted" made the row leave the list and come back behind the dialog.
 *
 * ⚠ `admin` and no `url` (null or empty) is the router's own rule for a root terminal
 * (`OpensAsRoot`). The router can still ACCEPT this call, when the requester already controls the
 * node: the store then shows the answer after the call.
 */
export function waitsForRootAnswer(
  service: { admin?: boolean; url?: string | null } | undefined,
  confirmedRoot: boolean
): boolean {
  return !confirmedRoot && !!service?.admin && !service.url
}

/** What the store does after a refused `service_share approve`. */
export type ApproveFailure = { ask: RootApproval } | { error: string }

/**
 * Decides what a refused approve becomes: the root dialog, or one sentence on the page.
 *
 * - Only the FIRST call may open the dialog. A confirmed call that the router still refuses is an
 *   error, or the dialog opens again with no end.
 * - One confirmation at a time. When another request's dialog is open (`openGrantId`), this
 *   request is not lost in silence: the page says to accept it again.
 */
export function afterApproveFailure(
  error: unknown,
  confirmedRoot: boolean,
  grantId: string,
  openGrantId: string | null
): ApproveFailure {
  const root = confirmedRoot ? null : rootApprovalNeeded(error)
  if (!root) return { error: approveRefusal(error, confirmedRoot) }
  if (openGrantId && openGrantId !== grantId) return { error: ROOT_APPROVAL_BUSY }
  return { ask: root }
}

/** What the page says when a second request needs the root confirmation while one is open. */
export const ROOT_APPROVAL_BUSY =
  'Another request is waiting for its root confirmation. Answer that one, then accept this request again.'

/** What the page says for a root refusal that `rootApprovalNeeded` could not read. */
export const ROOT_APPROVAL_UNREADABLE =
  'This request needs a root confirmation, and this version of the app cannot give it. Reload the page, then accept the request again.'

/**
 * One sentence for a refused `service_share approve` that is not the root confirmation.
 *
 * ⚠ The router's text for a root refusal is for an API caller, and it must not reach the page
 * (app#461). `rootApprovalNeeded` reads the router's sentence as it is today. If the router
 * changes that sentence, a refusal that still names `confirm_root` gets the app's own sentence
 * here, never the router's paragraph.
 */
export function approveRefusal(error: unknown, confirmedRoot = false): string {
  const failure = error as ToolFailure
  const asksRoot =
    record(failure?.details)?.confirm_root_required === true || (failure?.message ?? '').includes('confirm_root')
  if (failure?.code === 'failed_precondition' && asksRoot) {
    // ⚠ After the reader confirmed, "the app cannot give it" is false: the app gave it.
    return confirmedRoot ? 'Could not accept the request.' : ROOT_APPROVAL_UNREADABLE
  }
  return toolRefusalMessage(error, SERVICE_FORBIDDEN, 'Could not accept the request.')
}

/** The sentence a reader who is not the node's controller gets, for a node change. */
export const NODE_FORBIDDEN = 'Only the owner of this node, or an owner or admin of its organization, can change it.'

/** The sentence a reader who is not the network's manager gets, for a network change. */
export const NETWORK_FORBIDDEN =
  'Only the owner of this network, or an owner or admin of its organization, can change it.'

/** The sentence a reader who is not the node's controller gets, for a removal. */
export const NODE_REMOVE_FORBIDDEN =
  'Only the owner of this node, or an owner or admin of its organization, can remove it.'

/** `node_delete` refused a node registered by hand that the router still sees connected. */
export const NODE_STILL_CONNECTED =
  'This node is still connected to the router. Stop the agent on the machine, or wait until the node shows offline, then remove it again.'

/**
 * One sentence for a refused `node_delete`.
 *
 * ⚠ `failed_precondition` is an ONLINE node registered by hand, sent without `force`. The page
 * sends `force` when it reads the node online, so this is a node the page read as offline while
 * the router still holds its connection (for example, a node held below its channel's floor). The
 * router's text is for an API caller ("retry with force: true") and is not shown.
 */
export function nodeRemoveRefusal(error: unknown): string {
  if ((error as ToolFailure)?.code === 'failed_precondition') return NODE_STILL_CONNECTED
  return toolRefusalMessage(error, NODE_REMOVE_FORBIDDEN, 'Could not remove the node.')
}

/** The sentence a reader who may not delete the network gets. */
export const NETWORK_DELETE_FORBIDDEN =
  'Only the owner of this network, or an owner or admin of its organization, can delete it.'

/** One sentence for a refused `network_delete`. ⚠ A network with nodes is not refused (router#2139). */
export function networkRemoveRefusal(error: unknown): string {
  return toolRefusalMessage(error, NETWORK_DELETE_FORBIDDEN, 'Could not delete the network.')
}
